<template>
  <div class="container mt-4 mb-5" style="max-width: 980px;">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3 class="m-0">Benutzereinstellungen</h3>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'Invoices' }">
        Zur&uuml;ck
      </router-link>
    </div>

    <div v-if="!userStore.me" class="alert alert-secondary">
      Lade Benutzerdaten...
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <h5 class="card-title mb-3">Profil</h5>
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">Name</label>
            <input class="form-control" :value="userStore.displayName || '-'" disabled />
          </div>
          <div class="col-md-6">
            <label class="form-label">E-Mail</label>
            <input class="form-control" :value="userStore.email || '-'" disabled />
          </div>
        </div>
        <div class="text-muted small mt-2" v-if="userStore.roles && userStore.roles.length">
          Rollen: <code>{{ userStore.roles.join(', ') }}</code>
        </div>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <h5 class="card-title mb-3">Einstellungen</h5>
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label" for="settingsCompany">Aktuelle Firma</label>
            <select id="settingsCompany" v-model="form.currentCompanyId" class="form-select">
              <option :value="null">Automatisch</option>
              <option v-for="c in userStore.companies" :key="c.id" :value="c.id">
                {{ c.name || c.companyNumber || c.id }}
              </option>
            </select>
            <div class="text-muted small mt-1">
              Dieser Wert steuert, welche Daten in den Modulen angezeigt werden (Mandant).
            </div>
          </div>

          <div class="col-md-3">
            <label class="form-label" for="settingsLocale">Sprache</label>
            <select id="settingsLocale" v-model="form.locale" class="form-select">
              <option :value="null">Automatisch</option>
              <option v-for="availableLocale in availableLocales" :key="`loc-${availableLocale}`" :value="availableLocale">
                {{ availableLocale.toUpperCase() }}
              </option>
            </select>
          </div>

          <div class="col-md-3">
            <label class="form-label" for="settingsTheme">Theme</label>
            <select id="settingsTheme" v-model="form.theme" class="form-select">
              <option :value="null">Firma (Default)</option>
              <option v-for="t in themes" :key="t.id" :value="t.id">
                {{ t.label }}
              </option>
            </select>
            <div class="text-muted small mt-1">
              Aktuell: <code>{{ effectiveThemeLabel }}</code>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-4">
          <button class="btn btn-outline-secondary" type="button" @click="resetForm">
            Zur&uuml;cksetzen
          </button>
          <button class="btn btn-primary" type="button" :disabled="!isDirty || savingSettings" @click="saveSettings">
            Speichern
          </button>
        </div>

        <div v-if="savedSettings" class="alert alert-success mt-3 mb-0">
          Einstellungen gespeichert.
        </div>
      </div>
    </div>

    <div class="card mb-4" v-if="apiUsage">
      <div class="card-body">
        <h5 class="card-title mb-3">API-Nutzung</h5>
        <div class="alert mb-3" :class="apiUsageAlertClass">
          {{ apiUsage.message }}
        </div>

        <div class="d-flex flex-wrap gap-2 mb-3">
          <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'ApiPricingHelp' }">
            API-Preisübersicht
          </router-link>
        </div>

        <div class="row g-3">
          <div class="col-md-3">
            <label class="form-label">Plan</label>
            <input class="form-control" :value="apiUsage.planDisplayName || apiUsage.planCode || '-'" disabled />
          </div>
          <div class="col-md-3">
            <label class="form-label">Modus</label>
            <input class="form-control" :value="formatMode(apiUsage.currentMode)" disabled />
          </div>
          <div class="col-md-3">
            <label class="form-label">Credits übrig</label>
            <input class="form-control" :value="String(apiUsage.includedCreditsRemaining)" disabled />
          </div>
          <div class="col-md-3">
            <label class="form-label">Inkludierte Credits / Monat</label>
            <input class="form-control" :value="String(apiUsage.includedCreditsLimit)" disabled />
          </div>
        </div>

        <div class="row g-3 mt-1">
          <div class="col-md-4">
            <label class="form-label">Credits genutzt</label>
            <input class="form-control" :value="String(apiUsage.includedCreditsUsed)" disabled />
          </div>
          <div class="col-md-4">
            <label class="form-label">Credits reserviert</label>
            <input class="form-control" :value="String(apiUsage.includedCreditsPending)" disabled />
          </div>
          <div class="col-md-4">
            <label class="form-label">Overage-Modell</label>
            <input class="form-control" :value="formatOverageMode(apiUsage.overageMode)" disabled />
          </div>
        </div>

        <div class="row g-3 mt-1">
          <div class="col-md-4">
            <label class="form-label">Overage-Credits genutzt</label>
            <input class="form-control" :value="String(apiUsage.overageCreditsUsed)" disabled />
          </div>
          <div class="col-md-4">
            <label class="form-label">Overage-Credits übrig</label>
            <input class="form-control" :value="formatNullable(apiUsage.overageCreditsRemaining)" disabled />
          </div>
          <div class="col-md-4">
            <label class="form-label">Monatliches Overage-Cap</label>
            <input class="form-control" :value="formatNullable(apiUsage.maxOverageCreditsPerMonth)" disabled />
          </div>
        </div>

        <div class="text-muted small mt-3">
          Zeitraum: {{ formatDate(apiUsage.billingPeriodStartLocal) }} bis {{ formatDate(apiUsage.billingPeriodEndLocalExclusive) }}
          ({{ apiUsage.timeZoneId }})
        </div>
      </div>
    </div>

    <div v-if="isDeveloperMenuVisible" class="card">
      <div class="card-body">
        <h5 class="card-title mb-3">Entwicklermen&uuml;</h5>

        <div class="d-flex flex-wrap gap-2 mb-4">
          <button class="btn btn-outline-primary btn-sm" type="button" @click="openLink(docsUrl)">
            Docs &ouml;ffnen
          </button>
          <button
            class="btn btn-outline-primary btn-sm"
            type="button"
            :disabled="!swaggerEnabled"
            @click="openLink(swaggerUrl)"
          >
            Swagger &ouml;ffnen
          </button>
        </div>

        <div class="text-muted small mb-4">
          Swagger aktiviert: <strong>{{ swaggerEnabled ? 'Ja' : 'Nein' }}</strong>
        </div>

        <h6 class="mb-2">Personal Access Tokens</h6>
        <div class="row g-2 align-items-end mb-3">
          <div class="col-md-6">
            <label class="form-label" for="patName">Name</label>
            <input id="patName" v-model="tokenForm.name" class="form-control" maxlength="120" placeholder="z.B. CI Script" />
          </div>
          <div class="col-md-3">
            <label class="form-label" for="patLifetimeDays">Lifetime (Tage, optional)</label>
            <input
              id="patLifetimeDays"
              v-model.number="tokenForm.lifetimeDays"
              type="number"
              min="1"
              class="form-control"
              placeholder="ohne Ablauf"
            />
          </div>
          <div class="col-md-3 d-grid">
            <button
              class="btn btn-primary"
              type="button"
              :disabled="creatingToken || !tokenForm.name.trim()"
              @click="createToken"
            >
              Token erstellen
            </button>
          </div>
        </div>

        <div v-if="createdTokenValue" class="alert alert-warning">
          <div class="fw-semibold mb-1">Neues Token (nur jetzt sichtbar):</div>
          <code class="d-block user-select-all mb-2">{{ createdTokenValue }}</code>
          <button class="btn btn-sm btn-outline-dark" type="button" @click="copyCreatedToken">
            Token kopieren
          </button>
        </div>

        <div class="table-responsive">
          <table class="table table-sm align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Prefix</th>
                <th>Erstellt</th>
                <th>L&auml;uft ab</th>
                <th>Letzte Nutzung</th>
                <th>Status</th>
                <th class="text-end">Aktion</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!tokens.length">
                <td colspan="7" class="text-muted text-center py-3">Keine Tokens vorhanden.</td>
              </tr>
              <tr v-for="token in tokens" :key="token.id">
                <td>{{ token.name }}</td>
                <td><code>{{ token.tokenPrefix }}</code></td>
                <td>{{ formatDate(token.creationTimestamp) }}</td>
                <td>{{ formatDate(token.expiresAtUtc) }}</td>
                <td>{{ formatDate(token.lastUsedAtUtc) }}</td>
                <td>
                  <span v-if="token.revokedAtUtc" class="badge text-bg-secondary">Widerrufen</span>
                  <span v-else-if="isExpired(token.expiresAtUtc)" class="badge text-bg-danger">Abgelaufen</span>
                  <span v-else class="badge text-bg-success">Aktiv</span>
                </td>
                <td class="text-end">
                  <button
                    class="btn btn-outline-danger btn-sm"
                    type="button"
                    :disabled="!!token.revokedAtUtc || revokingTokenId === token.id"
                    @click="revokeToken(token.id)"
                  >
                    Widerrufen
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { THEMES } from "@/themes/themeRegistry";
import { useUserStore } from "@/stores/userStore";

const userStore = useUserStore();
const { availableLocales, locale: i18nLocale } = useI18n();

const themes = THEMES;

const initial = () => ({
  currentCompanyId: userStore.currentCompanyId || null,
  locale: userStore.locale || null,
  theme: userStore.theme || null,
});

const form = reactive(initial());
const savedSettings = ref(false);
const savingSettings = ref(false);

const tokenForm = reactive({
  name: "",
  lifetimeDays: null,
});
const creatingToken = ref(false);
const revokingTokenId = ref(null);
const createdTokenValue = ref("");

const isDeveloperMenuVisible = computed(() => userStore.isDeveloperMenuVisible);
const tokens = computed(() => userStore.personalAccessTokens || []);
const swaggerEnabled = computed(() => !!userStore.developerOptions?.swaggerEnabled);
const swaggerUrl = computed(() => userStore.developerOptions?.swaggerUrl || "/api/swagger");
const docsUrl = computed(() => userStore.developerOptions?.docsUrl || "/docs/");
const apiUsage = computed(() => userStore.apiUsage);
const apiUsageAlertClass = computed(() => {
  switch (apiUsage.value?.currentMode) {
    case "free":
      return "alert-success";
    case "paid":
      return "alert-warning";
    case "blocked":
      return "alert-danger";
    default:
      return "alert-secondary";
  }
});

const isDirty = computed(() => (
  (form.currentCompanyId || null) !== (userStore.currentCompanyId || null) ||
  (form.locale || null) !== (userStore.locale || null) ||
  (form.theme || null) !== (userStore.theme || null)
));

const effectiveThemeLabel = computed(() => {
  const explicit = form.theme || userStore.theme;
  if (explicit) {
    return explicit;
  }
  return userStore.currentCompany?.defaultTheme || "EHW";
});

function resetForm() {
  const next = initial();
  form.currentCompanyId = next.currentCompanyId;
  form.locale = next.locale;
  form.theme = next.theme;
  savedSettings.value = false;
}

async function saveSettings() {
  savingSettings.value = true;
  savedSettings.value = false;
  try {
    await userStore.updateMySettings({
      currentCompanyId: form.currentCompanyId,
      locale: form.locale,
      theme: form.theme,
    });

    userStore.applyTheme();
    if (userStore.locale) {
      i18nLocale.value = userStore.locale;
    }
    savedSettings.value = true;
  } finally {
    savingSettings.value = false;
  }
}

async function loadDeveloperData() {
  if (!isDeveloperMenuVisible.value) {
    return;
  }

  await userStore.loadDeveloperOptions();
  await userStore.loadPersonalAccessTokens();
}

async function createToken() {
  if (!tokenForm.name.trim()) {
    return;
  }

  creatingToken.value = true;
  try {
    const payload = {
      name: tokenForm.name.trim(),
      lifetimeDays: tokenForm.lifetimeDays || null,
    };

    const created = await userStore.createPersonalAccessToken(payload);
    createdTokenValue.value = created?.token || "";
    tokenForm.name = "";
    tokenForm.lifetimeDays = null;
  } finally {
    creatingToken.value = false;
  }
}

async function revokeToken(tokenId) {
  revokingTokenId.value = tokenId;
  try {
    await userStore.revokePersonalAccessToken(tokenId);
  } finally {
    revokingTokenId.value = null;
  }
}

function formatDate(value) {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }

  return parsed.toLocaleString();
}

function formatNullable(value) {
  return value === null || value === undefined ? "unbegrenzt" : String(value);
}

function formatMode(value) {
  switch (value) {
    case "free":
      return "kostenfrei";
    case "paid":
      return "kostenpflichtig";
    case "blocked":
      return "blockiert";
    default:
      return "deaktiviert";
  }
}

function formatOverageMode(value) {
  return value === "PayPerRequest" ? "Pay per request" : "Blockieren";
}

function isExpired(value) {
  if (!value) {
    return false;
  }

  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime()) && parsed <= new Date();
}

function openLink(url) {
  if (!url) {
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

async function copyCreatedToken() {
  if (!createdTokenValue.value) {
    return;
  }

  try {
    await navigator.clipboard.writeText(createdTokenValue.value);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to copy token to clipboard:", error);
  }
}

onMounted(async () => {
  if (!userStore.me) {
    try {
      await userStore.loadMe();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to load /user/me:", error);
    }
  }

  resetForm();

  try {
    await loadDeveloperData();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to load developer menu data:", error);
  }
});
</script>
